
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { getContentTypeIcon, isHighlightedText, getHighlightedText } from "./contentCardUtils";
import { useState, useEffect } from "react";

interface CardThumbnailProps {
  thumbnailUrl?: string;
  contentType: string;
  title?: string;
  aspectRatio?: number;
  contentItem?: any; // Full content item for highlighted text detection
}

const CardThumbnail = ({ 
  thumbnailUrl, 
  contentType, 
  title,
  aspectRatio = 16/9,
  contentItem
}: CardThumbnailProps) => {
  const [imageError, setImageError] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  
  // Check if this is highlighted text content
  const isHighlight = contentItem && isHighlightedText(contentItem);
  const highlightedText = contentItem ? getHighlightedText(contentItem) : null;
  
  // Reset error state when thumbnailUrl changes
  useEffect(() => {
    if (thumbnailUrl) {
      // Create a full URL if it's a relative path
      let fullUrl: string;
      
      if (thumbnailUrl.startsWith('http') || thumbnailUrl.startsWith('data:')) {
        // Already an absolute URL or data URL
        fullUrl = thumbnailUrl;
      } else {
        // Handle relative paths consistently
        const origin = window.location.origin;
        fullUrl = thumbnailUrl.startsWith('/') 
          ? `${origin}${thumbnailUrl}`
          : `${origin}/${thumbnailUrl}`;
      }
      
      console.log(`CardThumbnail processing image: ${thumbnailUrl} -> ${fullUrl}`);
      
      setImageUrl(fullUrl);
      setImageError(false);
      
      // For data URLs (base64), don't preload
      if (thumbnailUrl.startsWith('data:')) {
        return;
      }
      
      // Preload image to check if it loads correctly
      const img = new Image();
      img.src = fullUrl;
      
      img.onerror = () => {
        console.error(`Failed to load image: ${fullUrl}`);
        setImageError(true);
      };
      
      img.onload = () => {
        console.log(`Successfully loaded image: ${fullUrl}`);
      };
    } else {
      // Reset state when thumbnailUrl is undefined
      setImageUrl(undefined);
      setImageError(false);
    }
  }, [thumbnailUrl]);
  
  // Special handling for highlighted text with text-based thumbnails
  if (isHighlight && highlightedText) {
    // If we have a thumbnail URL, try to display it (might be text-based thumbnail)
    if (imageUrl && !imageError) {
      return (
        <AspectRatio ratio={aspectRatio} className="overflow-hidden">
          <img 
            src={imageUrl} 
            alt={`Highlighted text: ${highlightedText}`} 
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={() => {
              console.error(`Highlighted text thumbnail failed to load: ${imageUrl}`);
              setImageError(true);
            }}
            loading="lazy"
          />
        </AspectRatio>
      );
    }
    
    // Fallback: Show highlighted text with yellow background - NO BADGE
    return (
      <AspectRatio ratio={aspectRatio} className="bg-yellow-50 border border-yellow-200">
        <div className="flex h-full w-full items-center justify-center p-4">
          <div className="text-center">
            <div className="text-2xl mb-2">📌</div>
            <div className="text-sm text-gray-800 line-clamp-3 leading-relaxed bg-yellow-200 p-2 rounded font-medium">
              "{highlightedText}"
            </div>
          </div>
        </div>
      </AspectRatio>
    );
  }
  
  // Standard thumbnail handling for non-highlighted content
  if (imageUrl && !imageError) {
    return (
      <AspectRatio ratio={aspectRatio} className="overflow-hidden bg-gray-100">
        <img 
          src={imageUrl} 
          alt={title || "Content thumbnail"} 
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
          onError={() => {
            console.error(`Image failed to load in img tag: ${imageUrl}`);
            setImageError(true);
          }}
          loading="lazy"
        />
      </AspectRatio>
    );
  }
  
  // Fallback for when there's no thumbnail or it failed to load
  return (
    <AspectRatio ratio={aspectRatio} className="bg-gray-100">
      <div className="flex h-full w-full items-center justify-center text-gray-400">
        <span className="text-3xl">{getContentTypeIcon(contentType)}</span>
      </div>
    </AspectRatio>
  );
};

export default CardThumbnail;
